import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import { useDispatch, useSelector } from '_common/hooks';
import { InstanceService } from '_common/services';
import { viewPersonalSpaces } from 'Storage/pages/SpacesListPage/redux/spacesListPageSlice';
import { listObjects } from '_common/components/Table/TableSlice';
import { navigateToObject } from 'router/history';

import { IntlErrorBoundary, NodeIcon, UsernameLabel } from '_common/components';

import Header from './Header';

import {
  ThunksTable,
  Cell,
  Footer,
  type TableColumn,
  type TableRow,
  type onDoubleClickHandler,
} from '_common/components/Table';

import SelectionCounter from '_common/components/Table/Footer/SelectionCounter';

import styles from './PersonalSpacesListPage.module.scss';
const PAGE_IDENTITY = 'personal_spaces';

const PersonalSpacesListPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const appData = useSelector((state) => state.app.data);
  const list = useSelector((state) => state.table.identity.personal_spaces.list);

  //#region Table object listing handlers
  const fetchObjects = useCallback(async (parameters: any) => {
    const res = await new InstanceService().listSpaces({ ...parameters, personals: true });
    return res;
  }, []);

  useEffect(() => {
    dispatch(
      listObjects({
        identity: PAGE_IDENTITY,
        fetch: fetchObjects,
        cause: 'INITIAL',
        request: { offset: 0 },
      }),
    );
  }, []);

  // No useEffectOnUpdate since fetchObjects doesn't have any dependencies
  // Add if dependencies are added to fetchObjects
  //#endregion

  useEffect(() => {
    return () => {
      dispatch(viewPersonalSpaces(false));
    };
  }, []);

  const handleOpenDeleteSpaceModal = (object: Objekt) => {
    dispatch(
      openAndUpdateModal({
        modal: 'ConfirmationModal',
        data: {
          title: 'DELETE_ITEM_QUANTITY',
          titleValues: { total: 1 },
          message: 'DELETING_THE_SELECTED_ITEM_QUANTITY_WILL_PERMANENTLY_REMOVE_CONFIRM',
          messageValues: { total: 1 },
          confirmButtonTextId: 'global.delete',
          confirmButtonType: 'danger',
          cancelButtonTextId: 'global.cancel',
          actionCode: 'deleteObjects',
          actionValue: { objectList: [object], identity: 'spaces' },
          cancelButtonShow: true,
        },
      }),
    );
  };

  const columns = useMemo<TableColumn<Objekt>[]>(() => {
    return [
      {
        id: 'name',
        accessorKey: 'name',
        minWidth: 296,
        header: intl.formatMessage({ id: 'storage.browserHeader.name' }),
        cell: (info) => {
          const object = info.row.original;

          return (
            <Cell testId={`${object.id}-name-column`}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                {object.personal ? (
                  <>
                    <NodeIcon object={object} />
                    <div
                      style={{
                        paddingLeft: '2rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <UsernameLabel userId={object?.owner} />
                    </div>
                  </>
                ) : (
                  <Cell.ObjectName
                    object={object}
                    testId={`personal-spaces-${object.id}-object-name`}
                  />
                )}
              </div>
            </Cell>
          );
        },
      },
      {
        id: 'time.modification',
        accessorKey: 'time.modification',
        size: 192,
        header: intl.formatMessage({ id: 'storage.browserHeader.dateModified' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-date-modified-column`}>
              <Cell.DateTime
                date={{ date: object.time.modification }}
                time={{ time: object.time.modification }}
              />
            </Cell>
          );
        },
      },
      {
        id: 'sharedWith',
        accessorKey: 'shared_with',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({ id: 'storage.browserHeader.sharedWith' }),
        cell: (info) => {
          const object = info.row.original;
          return (
            <Cell testId={`${object.id}-shared-column`}>
              <Cell.Collaborators object={object} personalLabel />
            </Cell>
          );
        },
      },
    ];
  }, []);

  const rowsData = useMemo(() => {
    return list?.map<TableRow<Objekt>>((objectId) => {
      const object = appData[objectId];
      return {
        ...object,
        suffix: {
          actions: [
            {
              label: intl.formatMessage({ id: 'spaces.deleteSpace' }),
              disabled: object.personal,
              onClick: () => {
                handleOpenDeleteSpaceModal(object);
              },
            },
          ],
        },
      };
    });
  }, [list, appData]);

  const handleRowDoubleClick: onDoubleClickHandler<Objekt> = (row) => {
    const { id } = row as Objekt;
    navigateToObject('space', id);
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <IntlErrorBoundary size="s" style={{ margin: '0 0 0 7rem' }}>
          <Header />
        </IntlErrorBoundary>
      </div>
      <div className={styles.content}>
        <ThunksTable
          identity={PAGE_IDENTITY}
          options={{ columns, data: rowsData }}
          fetchObjects={fetchObjects}
          onDoubleClick={handleRowDoubleClick}
          loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
          renderFooter={() => (
            <Footer>
              <SelectionCounter identity={PAGE_IDENTITY} />
            </Footer>
          )}
          testId="personal-spaces-table"
        />
      </div>
    </div>
  );
};

export default PersonalSpacesListPage;
